import { z } from 'zod';
import { FAILURE_MODE_CODES, POSITION_CODES, VMRS_SUBSYSTEM_CODES, positionRefinement } from '../catalog/failure-codes.js';
/**
 * E1 — Eventos corretivos (RF-01 a RF-06).
 *
 * O evento é a porta de entrada do ciclo que o PRD chama de núcleo do MKBF:
 * falha → triagem → fila → OS → portões → liberação.
 */
export const EventOrigin = {
    CCO: 'CCO',
    OPERATOR: 'OPERATOR',
    GARAGE: 'GARAGE',
    INSPECTION: 'INSPECTION',
    CLEANING: 'CLEANING',
};
export const EVENT_ORIGIN_LABELS = {
    CCO: 'CCO',
    OPERATOR: 'Operador',
    GARAGE: 'Garagem',
    INSPECTION: 'Inspeção',
    CLEANING: 'Limpeza',
};
export const EventStatus = {
    REGISTERED: 'REGISTERED',
    IN_TRIAGE: 'IN_TRIAGE',
    FIELD_SERVICE: 'FIELD_SERVICE',
    RECALLED: 'RECALLED',
    DEFERRED: 'DEFERRED',
    IN_MAINTENANCE: 'IN_MAINTENANCE',
    CLOSED: 'CLOSED',
    CANCELLED: 'CANCELLED',
};
export const EVENT_STATUS_LABELS = {
    REGISTERED: 'Registrado',
    IN_TRIAGE: 'Em triagem',
    FIELD_SERVICE: 'Socorro em campo',
    RECALLED: 'Recolhido',
    DEFERRED: 'Deferido',
    IN_MAINTENANCE: 'Em manutenção',
    CLOSED: 'Encerrado',
    CANCELLED: 'Cancelado',
};
/** RF-03 — os três destinos da triagem. */
export const TriageDestination = {
    FIELD: 'FIELD',
    RECALL: 'RECALL',
    DEFER: 'DEFER',
};
export const TRIAGE_DESTINATION_LABELS = {
    FIELD: 'Socorro em campo',
    RECALL: 'Recolher',
    DEFER: 'Deferir para a parada programada',
};
export const FieldOutcome = {
    RESOLVED_IN_FIELD: 'RESOLVED_IN_FIELD',
    TOWED: 'TOWED',
    RETURNED_UNDER_OWN_POWER: 'RETURNED_UNDER_OWN_POWER',
    NOT_FOUND: 'NOT_FOUND',
    CANCELLED: 'CANCELLED',
};
export const FIELD_OUTCOME_LABELS = {
    RESOLVED_IN_FIELD: 'Resolvido em campo',
    TOWED: 'Rebocado',
    RETURNED_UNDER_OWN_POWER: 'Retornou por meios próprios',
    NOT_FOUND: 'Carro não localizado',
    CANCELLED: 'Atendimento cancelado',
};
/**
 * Desfechos em que o carro volta a rodar sem passar pela garagem. Falha de
 * segurança nunca chega aqui: o RF-05 bloqueia antes.
 */
export const FIELD_OUTCOMES_RETURNING_TO_LINE = [
    FieldOutcome.RESOLVED_IN_FIELD,
    FieldOutcome.NOT_FOUND,
];
// --- Registro do evento (RF-01) --------------------------------------------
/** Código composto Modo × Subsistema × Posição (padrão de codificação de falhas). */
const failureCodeInput = {
    failureModeCode: z.enum(FAILURE_MODE_CODES).optional(),
    subsystemCode: z.enum(VMRS_SUBSYSTEM_CODES).optional(),
    position: z.enum(POSITION_CODES).optional(),
};
export const createEventSchema = z.object({
    vehicleId: z.string().uuid('Selecione o carro'),
    /** Atalho do catálogo (falhas frequentes). Opcional. */
    catalogItemId: z.string().uuid().optional(),
    ...failureCodeInput,
    reportedDescription: z.string().trim().max(500).optional(),
    origin: z.nativeEnum(EventOrigin).default(EventOrigin.CCO),
    operatorId: z.string().uuid().optional(),
    lineCode: z.string().trim().max(16).optional(),
    locationDescription: z.string().trim().max(255).optional(),
    latitude: z.coerce.number().min(-90).max(90).optional(),
    longitude: z.coerce.number().min(-180).max(180).optional(),
}).superRefine(positionRefinement('subsystemCode', 'position'));
export const eventQuerySchema = z.object({
    page: z.coerce.number().int().min(1).default(1),
    perPage: z.coerce.number().int().min(1).max(100).default(20),
    status: z.nativeEnum(EventStatus).optional(),
    vehicleId: z.string().uuid().optional(),
    /** Fila de triagem: só os que ainda esperam decisão. */
    pendingTriage: z.coerce.boolean().optional(),
    from: z.coerce.date().optional(),
    to: z.coerce.date().optional(),
});
// --- Triagem (RF-03) --------------------------------------------------------
export const triageSchema = z
    .object({
    destination: z.nativeEnum(TriageDestination),
    /** Obrigatório ao deferir: é decisão que adia atendimento. */
    reasonCodeId: z.string().uuid().optional(),
    note: z.string().trim().max(255).optional(),
    /** Confirmação da falha, quando o PCM reclassifica o que o CCO registrou. */
    catalogItemId: z.string().uuid().optional(),
    ...failureCodeInput,
})
    .refine((v) => v.destination !== TriageDestination.DEFER || Boolean(v.reasonCodeId), {
    message: 'Deferir exige código de motivo',
    path: ['reasonCodeId'],
})
    .superRefine(positionRefinement('subsystemCode', 'position'));
// --- Socorro em campo (RF-04) ----------------------------------------------
/** Apontamento por toque: cada passo é um botão, sem formulário. */
export const FieldStep = {
    ARRIVED: 'ARRIVED',
    STARTED: 'STARTED',
    FINISHED: 'FINISHED',
};
export const fieldStepSchema = z.object({
    step: z.nativeEnum(FieldStep),
});
export const fieldOutcomeSchema = z.object({
    outcome: z.nativeEnum(FieldOutcome),
    confirmedCatalogItemId: z.string().uuid().optional(),
    /** Constatação em campo, no mesmo código do registro — base do alinhamento origem × diagnóstico. */
    confirmedFailureModeCode: z.enum(FAILURE_MODE_CODES).optional(),
    confirmedSubsystemCode: z.enum(VMRS_SUBSYSTEM_CODES).optional(),
    confirmedPosition: z.enum(POSITION_CODES).optional(),
    note: z.string().trim().max(500).optional(),
    materials: z
        .array(z.object({
        materialId: z.string().uuid(),
        quantity: z.coerce.number().positive(),
        serialNumber: z.string().trim().max(64).optional(),
    }))
        .default([]),
})
    .superRefine(positionRefinement('confirmedSubsystemCode', 'confirmedPosition'));
export const dispatchFieldServiceSchema = z.object({
    technicianId: z.string().uuid().optional(),
    supportVehicleCode: z.string().trim().max(16).optional(),
});
//# sourceMappingURL=event.schemas.js.map